/** Reduce work quickly when frames are slow; recover detail only after a stable run. */
export class AdaptivePreviewQuality {
  private index: number;
  private fastFrames = 0;
  private slowFrames = 0;
  private cooldown = 0;
  private average: number | null = null;

  constructor(
    private readonly levels = [384, 512, 640, 768, 960, 1152, 1280],
    private readonly initialSize = 768,
    private readonly budgetMs = 30
  ) {
    this.index = Math.max(0, levels.findIndex(size => size >= initialSize));
  }

  get size(): number { return this.levels[this.index]; }

  reset(): void {
    this.index = Math.max(0, this.levels.findIndex(size => size >= this.initialSize));
    this.fastFrames = this.slowFrames = this.cooldown = 0;
    this.average = null;
  }

  record(milliseconds: number, maximum = Infinity): void {
    if (!Number.isFinite(milliseconds) || milliseconds < 0) return;
    const allowed = this.levels.reduce((last, size, index) => size <= maximum ? index : last, 0);
    this.index = Math.min(this.index, allowed);
    // The first frame includes asset upload / shader warm-up, not steady playback.
    if (this.average === null) { this.average = Math.min(milliseconds, this.budgetMs); return; }
    this.average = this.average * 0.7 + milliseconds * 0.3;
    this.cooldown = Math.max(0, this.cooldown - 1);
    this.slowFrames = this.average > this.budgetMs ? this.slowFrames + 1 : 0;
    if (milliseconds > this.budgetMs * 2 || this.slowFrames >= 2) {
      this.index = Math.max(0, this.index - (milliseconds > this.budgetMs * 3 ? 2 : 1));
      this.average = null;
      this.fastFrames = this.slowFrames = 0;
      this.cooldown = 30;
      return;
    }
    this.fastFrames = this.cooldown === 0 && this.average < this.budgetMs * 0.65 && milliseconds < this.budgetMs * 0.8
      ? this.fastFrames + 1 : 0;
    if (this.fastFrames >= 24) {
      this.index = Math.min(allowed, this.index + 1);
      this.fastFrames = 0;
    }
  }
}

export function getPausedFootageSize(targetPixels: number, zoom = 1, maximum = 1536): number {
  return Math.max(1, Math.min(maximum, Math.max(1024, Math.ceil(targetPixels * Math.max(1, zoom)))));
}
